<template>
  <div class="article-page">
    <HeadNav selected-language="en" page="security-guide" />

    <main class="article-main">
      <nav class="breadcrumb" aria-label="Breadcrumb">
        <a href="/">Home</a>
        <span aria-hidden="true">/</span>
        <a href="/security-guide/">Security Guide</a>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{{ article.h1 }}</span>
      </nav>

      <article class="guide-article">
        <header class="article-header">
          <h1>{{ article.h1 }}</h1>
        </header>

        <div class="article-body">
          <template v-for="(block, index) in article.blocks">
            <h2 v-if="block.type === 'h2'" :key="`h2-${index}`">{{ block.text }}</h2>
            <h3 v-else-if="block.type === 'h3'" :key="`h3-${index}`">{{ block.text }}</h3>
            <p v-else-if="block.type === 'p'" :key="`p-${index}`" v-html="block.html"></p>
            <ul v-else-if="block.type === 'ul'" :key="`ul-${index}`" class="guide-list">
              <li v-for="(item, itemIndex) in block.items" :key="`ul-${index}-${itemIndex}`" v-html="item"></li>
            </ul>
            <ol v-else-if="block.type === 'ol'" :key="`ol-${index}`" class="guide-list guide-list--ordered">
              <li v-for="(item, itemIndex) in block.items" :key="`ol-${index}-${itemIndex}`" v-html="item"></li>
            </ol>
            <div v-else-if="block.type === 'table'" :key="`table-${index}`" class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th v-for="header in block.headers" :key="header">{{ header }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIndex) in block.rows" :key="rowIndex">
                    <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </article>

      <nav class="article-pagination" aria-label="Article navigation">
        <a :href="previousArticle.path" class="pagination-link pagination-link--previous">
          <span class="pagination-label">
            <i class="fa fa-arrow-left" aria-hidden="true"></i>
            Previous article
          </span>
          <strong>{{ previousArticle.h1 }}</strong>
        </a>
        <a :href="nextArticle.path" class="pagination-link pagination-link--next">
          <span class="pagination-label">
            Next article
            <i class="fa fa-arrow-right" aria-hidden="true"></i>
          </span>
          <strong>{{ nextArticle.h1 }}</strong>
        </a>
      </nav>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import HeadNav from '@/components/HeadNav.vue';
import securityArticles from '@/constants/securityArticles';

export default {
  name: 'SecurityArticle',
  components: { HeadNav },
  computed: {
    article() {
      const slug = this.$route.meta.articleSlug;
      return securityArticles.find((item) => item.slug === slug) || securityArticles[0];
    },
    articleIndex() {
      return securityArticles.findIndex((item) => item.slug === this.article.slug);
    },
    previousArticle() {
      const index = (this.articleIndex - 1 + securityArticles.length) % securityArticles.length;
      return securityArticles[index];
    },
    nextArticle() {
      const index = (this.articleIndex + 1) % securityArticles.length;
      return securityArticles[index];
    },
  },
};
</script>

<style lang="scss" scoped>
.article-page {
  min-height: 100vh;
  background: #f5f8f7;
  color: #14213d;
}

.article-main {
  width: min(920px, calc(100% - 32px));
  margin: 0 auto;
  padding: 24px 0 64px;
}

.breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 4px 0 18px;
  color: #64748b;
  font-size: 13px;

  a {
    color: #0b6d47;
    font-weight: 600;
    text-decoration: none;
  }

  span[aria-current='page'] {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.guide-article {
  overflow: hidden;
  border: 1px solid #dfe9e4;
  border-radius: 9px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(23, 70, 52, 0.06);
}

.article-header {
  padding: 42px 46px 34px;
  border-bottom: 1px solid #e7efeb;
  background: linear-gradient(140deg, #f6fcf9 0%, #fff 72%);
}

.article-header h1 {
  max-width: 760px;
  margin: 0;
  color: #102b21;
  font-size: clamp(32px, 5vw, 48px);
  line-height: 1.15;
  letter-spacing: -0.02em;
}

.article-body {
  padding: 14px 46px 42px;
  color: #41554d;
  font-size: 16px;
  line-height: 1.8;

  > h2 {
    margin: 30px 0 14px;
    color: #173b2e;
    font-size: 24px;
    line-height: 1.35;
  }

  > h3 {
    margin: 24px 0 10px;
    color: #173b2e;
    font-size: 19px;
    line-height: 1.4;
  }

  > p {
    margin: 0 0 16px;
  }

  ::v-deep a {
    color: #2563eb !important;
    font-weight: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover,
    &:focus {
      color: #1d4ed8 !important;
    }
  }
}

.guide-list {
  margin: 2px 0 18px;
  padding: 0;
  list-style: none !important;
}

.guide-list li {
  position: relative;
  margin-bottom: 10px;
  padding-left: 24px;
  color: #41554d;
  font-size: 16px;
  line-height: 1.7;
}

.guide-list li::before {
  position: absolute;
  top: 0.78em;
  left: 2px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #28a873;
  content: '';
  transform: translateY(-50%);
}

.guide-list--ordered {
  padding-left: 24px;
  list-style: decimal !important;
}

.guide-list--ordered li::before {
  display: none;
}

.article-pagination {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 20px;
}

.pagination-link {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  border: 1px solid #dfe9e4;
  border-radius: 8px;
  background: #fff;
  color: #14213d;
  text-decoration: none;
}

.pagination-link--next {
  text-align: right;
}

.pagination-label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #0b6d47;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.table-wrap {
  overflow-x: auto;
  margin: 12px 0 18px;
  border: 1px solid #e3ece8;
  border-radius: 8px;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th,
  td {
    padding: 12px 14px;
    border-bottom: 1px solid #e3ece8;
    text-align: left;
    vertical-align: top;
  }

  th {
    background: #f3faf6;
    color: #173b2e;
    font-weight: 700;
  }

  tr:last-child td {
    border-bottom: 0;
  }
}
</style>
